import React, { Component } from 'react';
import { connect } from 'react-redux';
import { removeShift } from 'redux/ducks/Post/Shifts/actions';
import { editEvent, publishDraft, saveAsDraft, saveLocalForm } from 'redux/ducks/Post/actions';
import { changeProfileField } from 'redux/ducks/Post/Profile';
import LocationList from 'scenes/V2/Shared/LocationList';
import { postValid, serviceFee, totalEventRate, eventDuration, completedEAP } from 'helpers/post';
import { trackMixpanelPageView } from 'helpers/post';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import { Button } from 'reactstrap';
import classNames from 'classnames';
import moment from 'moment';
import { validateForm } from 'redux/ducks/Post';
import BillingInfo from './BillingInfo';
import HorizontalRule from 'components/HorizontalRule';
import WhatHappensNext from './WhatHappensNext';
import { hasPaymentToPublish } from '../../../../helpers/payment';
import ToolTip from 'scenes/V2/ToolTip';
import { MILITARY_FORMAT } from 'helpers/datetime';

class PublishOrUpdateStep extends Component {
  constructor(props) {
    super(props);
    props.initialize('PublishOrUpdateStep');
    this.state = {
      locationIdx: 0,
      wasFieldValidated: {
        jobFinanceCode: false,
      },
      attemptedSubmit: false,
    };
  }

  componentDidMount() {
    if (!this.props.editMode) trackMixpanelPageView('create-event/post', this.props.currentUser);

    this.props.validateForm();
    this.props.saveLocalForm();
  }

  saveAsDraft = () => {
    trackMixpanelPageView('SaveAsDraftClick', this.props.currentUser);
    this.props.onSaveAsDraft(this.props.post, () => this.props.history.push('/dashboard'));
  };

  handleButtonClick = () => {
    const { editMode } = this.props;
    this.setState({ attemptedSubmit: true });

    if (editMode) {
      this.onUpdateEvent();
    } else {
      this.onPublish();
    }
  };

  onPublish = () => {
    if (this.publishDisabled()) return;
    trackMixpanelPageView('ListJobClick', this.props.currentUser);
    this.props.onPublish({ history: this.props.history });
  };

  publishDisabled = () => {
    const { payment, authenticated, post, currentUser } = this.props;
    const { shifts, eap, profile } = post;
    const hasCompletedEAP = completedEAP(eap, shifts);
    const eventCode = profile.eventCode || '';
    const jobCodeValid = !currentUser.jobCodeRequired || eventCode.trim().length > 0;

    return (
      !postValid(post) ||
      !authenticated ||
      !hasPaymentToPublish(payment, currentUser) ||
      !hasCompletedEAP ||
      !jobCodeValid ||
      !shifts.every(
        s => moment(s.startTime, MILITARY_FORMAT).isValid() && moment(s.endTime, MILITARY_FORMAT).isValid()
      ) ||
      shifts.some(
        s =>
          moment(s.startTime, MILITARY_FORMAT).isBefore(moment()) ||
          moment(s.endTime, MILITARY_FORMAT).isBefore(moment())
      )
    );
  };

  setPrecision = (amount, dec = 2) => Number(amount).toFixed(dec);

  showError = fieldName => {
    const { currentUser, post } = this.props;
    const { wasFieldValidated, attemptedSubmit } = this.state;

    if (fieldName === 'jobFinanceCode') {
      const eventCode = post.profile.eventCode || '';
      return (
        (wasFieldValidated.jobFinanceCode || attemptedSubmit) &&
        currentUser.jobCodeRequired &&
        !eventCode.trim()
      );
    }
    return false;
  };

  onFieldBlur = field => () => {
    if (!this.state.wasFieldValidated[field]) {
      this.setState(prevState => ({
        wasFieldValidated: {
          ...prevState.wasFieldValidated,
          [field]: true,
        },
      }));
    }
  };

  updateDisabled = () => {
    const {
      post: { shifts, eap, profile },
      currentUser,
      authenticated,
      payment,
    } = this.props;
    const hasCompletedEAP = completedEAP(eap, shifts);
    const eventCode = profile.eventCode || '';
    const jobCodeValid = !currentUser.jobCodeRequired || eventCode.trim().length > 0;

    return (
      !postValid(this.props.post) ||
      !authenticated ||
      !hasPaymentToPublish(payment, currentUser) ||
      !hasCompletedEAP ||
      !jobCodeValid ||
      !shifts.every(s => moment(s.startTime).isValid() && moment(s.endTime).isValid())
    );
  };

  onUpdateEvent = () => {
    if (this.updateDisabled()) return;
    this.props.onUpdateEvent({ history: this.props.history });
  };

  hasMultiDayShifts = () => this.props.post.shifts.some(shift => shift.isMultiDay);

  onFieldChange = event => {
    this.props.changeProfileField({
      name: 'eventCode',
      value: event.target.value,
    });

    if (this.state.attemptedSubmit) {
      this.setState({ attemptedSubmit: false });
    }
  };

  render() {
    const { authenticated, payment, currentUser, editMode, post } = this.props;
    const { locations, profile } = post;
    const shifts = post.shifts.filter(({ cancelled }) => !cancelled);
    const disabled = editMode ? this.updateDisabled() : this.publishDisabled();
    const eventServiceFee = serviceFee({ shifts, payment, currentUser });
    const totalPostRate = totalEventRate({ shifts });
    const eventCode = profile.eventCode || '';

    return (
      <div className="publish-or-update-step">
        <div className="nav-placeholder" />
        <main>
          <div className="row m-0">
            <div className="summary col-md-8">
              <header>
                <div className="screen-title">Summary</div>
                <HorizontalRule dashed margin="0" />
                <WhatHappensNext />
              </header>
              <div className="job-name">
                <div className="screen-title">{profile.title} BILLING ESTIMATE</div>
              </div>
              <LocationList
                locations={locations}
                shifts={shifts}
                onDeleteShift={this.props.onDeleteShift}
                editMode={editMode}
                history={this.props.history}
              />
            </div>
            <div className="billing-estimate col-md-4 p-2 pt-4">
              <div className="post-section">
                <div className="bg-gray">
                  <div className="title gotham-bold-font">NO LISTING FEES</div>
                </div>
                <div className="no-listing-text nimbus-regular-font">
                  There are no fees for listing a job or unfilled jobs. Go4 will charge the saved payment
                  method after a job has been worked.
                </div>
                <HorizontalRule margin="3px" />
                <div className="estimated-total-title gotham-bold-font">ESTIMATED TOTAL</div>
                <div className="event-info nimbus-regular-font">
                  {!this.hasMultiDayShifts() && (
                    <div className="info-item">
                      <p>Total Hours</p>
                      <p>{eventDuration({ shifts })}</p>
                    </div>
                  )}
                  <div className="info-item">
                    <p>Subtotal</p>
                    <p>${this.setPrecision(totalPostRate)}</p>
                  </div>
                  <div className="info-item">
                    <p>
                      Service Fee
                      <ToolTip
                        id="service-fee-info"
                        top="3px"
                        bodyText="Service fees cover platform operations, payment processing, job promotion, EAP creation & distribution, credentialing, and HIPAA-compliant injury record storage."
                      />
                    </p>
                    <p>${this.setPrecision(eventServiceFee)}</p>
                  </div>
                  <div className="info-item gotham-bold-font">
                    <p>ESTIMATED TOTAL</p>
                    <p>${this.setPrecision(totalPostRate + eventServiceFee)}</p>
                  </div>
                </div>
                <div className="job-finance-code-section">
                  <HorizontalRule margin="3px" />
                  <div className="form-group">
                    <label htmlFor="jobFinanceCode" className="gotham-bold-font">
                      Job Finance Code{!!currentUser.jobCodeRequired || '  (optional)'}
                      <ToolTip
                        id="tooltipJobFinanceCode"
                        top="3px"
                        fontSize="14px"
                        bodyText="Option to add an identifier code to this job. The code will only be visible on your Finance Export to help with reconciliation."
                      />
                    </label>
                    <input
                      className={classNames('form-control nimbus-regular-font', {
                        'is-invalid': this.showError('jobFinanceCode'),
                      })}
                      type="text"
                      name="jobFinanceCode"
                      id="jobFinanceCode"
                      value={eventCode}
                      onChange={this.onFieldChange}
                      onBlur={this.onFieldBlur('jobFinanceCode')}
                      maxLength="255"
                      required={!!currentUser.jobCodeRequired}
                    />
                    {this.showError('jobFinanceCode') && (
                      <div className="invalid-feedback nimbus-regular-font">Job code is required</div>
                    )}
                  </div>
                </div>
                <BillingInfo />
                <div className="actions">
                  <div onClick={this.handleButtonClick}>
                    <Button
                      className={classNames('actionBar__continue', {
                        disabled: disabled,
                      })}
                      disabled={disabled}
                      block
                      color="success"
                    >
                      <div>{editMode ? 'UPDATE JOB' : 'LIST JOB'}</div>
                    </Button>
                  </div>
                  {!editMode && (
                    <Link
                      className="actionBar__save__draft"
                      to="/v2/create-event/post"
                      onClick={this.saveAsDraft}
                      disabled={!authenticated}
                    >
                      <div>SAVE AS DRAFT</div>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  post: state.post,
  currentUser: state.session.currentUser,
  payment: state.payment,
  authenticated: state.session.authenticated,
});

const mapDispatchToProps = dispatch => ({
  onDeleteShift: (idx, reason) => dispatch(removeShift({ index: idx, reason })),
  onPublish: payload => dispatch(publishDraft(payload)),
  onSaveAsDraft: (post, callback) => dispatch(saveAsDraft({ post, callback, from: 'publish' })),
  onUpdateEvent: payload => dispatch(editEvent(payload)),
  validateForm: () => dispatch(validateForm('post')),
  saveLocalForm: () => dispatch(saveLocalForm()),
  changeProfileField: field => dispatch(changeProfileField(field)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PublishOrUpdateStep);
